import { SvgIconComponent } from '@mui/icons-material';
import PeopleIcon from '@mui/icons-material/People';
import StorefrontIcon from '@mui/icons-material/Storefront';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import ChecklistIcon from '@mui/icons-material/Checklist';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ReceiptIcon from '@mui/icons-material/Receipt';
import SettingsIcon from '@mui/icons-material/Settings';
import BusinessIcon from '@mui/icons-material/Business';
import { ModuleKey } from '../dashboard/registry/types';

export type ModuleGroup =
    | 'People'
    | 'Inventory & Procurement'
    | 'Finance'
    | 'Sales'
    | 'POS'
    | 'Settings';

/** Display order of the section headings on the launcher page. */
export const MODULE_GROUPS: ModuleGroup[] = [
    // POS leads: this product is a point-of-sale, and the inventory/finance/sales groups below are
    // the back office that supports it. Ordering them first read as a generic office-management app.
    'POS',
    'Inventory & Procurement',
    'Finance',
    'Sales',
    'People',
    'Settings',
];

export interface ModuleCard {
    /** Stable key — also the visibility-map key resolved by ModulesPage. */
    key: string;
    label: string;
    blurb: string;
    /** Landing route for the module; must exist in App.tsx. */
    path: string;
    /**
     * Overrides `path` per role, for a module whose reachable screens have disjoint role sets --
     * currently only POS (task-3 review F4: PosTerminalController admits InstitutionAdmin/
     * StoreAdmin/StoreManager; PosSettingsController GET admits InstitutionAdmin/StoreAdmin/
     * Accountant -- no single path both a StoreManager and an Accountant-without-store-role can
     * read). Receives the caller's raw role strings; falls back to `path` when absent or when it
     * returns undefined.
     */
    pathFor?: (roles: string[]) => string | undefined;
    icon: SvgIconComponent;
    group: ModuleGroup;
    /**
     * Institution module-gating key, when the module belongs to one. Cards carrying a
     * `gatedModule` disappear for tenants that have not enabled it. Deliberately checked
     * against the institution's `enabledModules` (a product seam that fails open), never
     * against `visibleModules` — the latter is derived from dashboard-widget coverage per
     * role and would hide modules from roles that simply own no widgets.
     */
    gatedModule?: ModuleKey;
}

/**
 * Every top-level module the app can navigate to, as shown on the /modules launcher.
 *
 * Pure data: no hooks, no permission logic. ModulesPage owns visibility so the permission
 * checks stay identical to the sidebar's.
 */
export const MODULE_CATALOG: ModuleCard[] = [
    // People
    { key: 'users', label: 'Users', blurb: 'Accounts, roles and per-user permissions.', path: '/users', icon: PeopleIcon, group: 'People' },

    // Inventory & Procurement
    { key: 'stores', label: 'Stores', blurb: 'Store locations and their staff.', path: '/stores', icon: StorefrontIcon, group: 'Inventory & Procurement', gatedModule: 'inventory' },
    { key: 'items', label: 'Items & Stock', blurb: 'Items, units, categories, stock in/out and history.', path: '/items', icon: Inventory2Icon, group: 'Inventory & Procurement', gatedModule: 'inventory' },
    { key: 'stocktakes', label: 'Stocktakes', blurb: 'Physical counts and variance adjustments.', path: '/stocktakes', icon: ChecklistIcon, group: 'Inventory & Procurement', gatedModule: 'inventory' },
    { key: 'stockTransfers', label: 'Stock Transfers', blurb: 'Move stock between stores.', path: '/stock-transfers', icon: SwapHorizIcon, group: 'Inventory & Procurement', gatedModule: 'inventory' },
    { key: 'procurement', label: 'Procurement', blurb: 'Suppliers, purchase orders, receipts, bills and payments.', path: '/procurement/purchase-orders', icon: ShoppingCartIcon, group: 'Inventory & Procurement' },

    // Finance
    { key: 'accounting', label: 'Accounting', blurb: 'Chart of accounts, journals, ledger and statements.', path: '/accounting/chart-of-accounts', icon: AccountBalanceIcon, group: 'Finance', gatedModule: 'accounting' },

    // Sales
    { key: 'sales', label: 'Sales', blurb: 'Quotations, invoices, customers and receivable ageing.', path: '/sales/invoices', icon: ReceiptLongIcon, group: 'Sales', gatedModule: 'sales' },

    // POS — ships ungated: 'pos' is not one of the three DashboardModule-backed gates
    // (accounting, inventory, sales), so a gatedModule key here would make
    // enabledModules.has('pos') always false and hide this card for every institution.
    // `path` points at '/pos/sales' (task 5): PosSaleQueryController's list/detail endpoints
    // admit InstitutionAdmin, StoreAdmin, StoreManager AND Accountant (verified against its
    // @PreAuthorize) -- every role that reaches this card at all -- so a single path is correct
    // for all of them and the earlier per-role `pathFor` override (which sent a store-roleless
    // Accountant to '/pos/settings' instead, since PosTerminalController's '/pos/terminals'
    // excludes Accountant) is no longer needed.
    {
        key: 'pos', label: 'POS', blurb: 'Counter settings, terminals, cashiers and till sales.',
        path: '/pos/sales', icon: PointOfSaleIcon, group: 'POS',
    },

    // Settings — matches the sidebar's Settings group, and stays last for the same reason.
    { key: 'billing', label: 'Billing', blurb: 'Subscription invoices and payment.', path: '/billing', icon: ReceiptIcon, group: 'Settings' },
    { key: 'institutionSettings', label: 'Institution Settings', blurb: 'Your institution’s own configuration.', path: '/institution-settings', icon: SettingsIcon, group: 'Settings' },
    { key: 'institutions', label: 'Institutions', blurb: 'All tenants, their users and their stores.', path: '/institutions', icon: BusinessIcon, group: 'Settings' },
];
